import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ActionIcon, Alert, Anchor, Badge, Group, Stack, Text, Title, Tooltip } from '@mantine/core';
import {
  IconAlertCircle,
  IconArrowDown,
  IconArrowLeft,
  IconArrowUp,
  IconBolt,
  IconMapPin,
  IconRefresh,
  IconUsers,
} from '@tabler/icons-react';
import DealStatusBadge from '@/components/app/deals/DealStatusBadge';
import NoNds from '@/components/common/NoNds';
import { goodsSpecFactDiff } from '@/lib/dealPrice';
import { forwardingStageTitleKey } from '@/lib/forwarding';
import { formatMoney, formatOrgName, formatProperty, headlineQuality } from '@/lib/format';
import { ROUTES } from '@/routes/paths';
import type { BargainStepsInfo } from '@/types/deals';
import NumberedId from '@/components/common/NumberedId';

interface DealHeaderProps {
  readonly info: BargainStepsInfo;
  readonly onRefresh: () => void;
  readonly refreshing: boolean;
  /** Компактное действие в правом верхнем углу (кнопка чата) — перед «Обновить». */
  readonly chatAction?: ReactNode;
}

// Шапка карточки сделки: назад, идентификатор (спецификация/контракт), товар/вес/цена, статус,
// текущая стадия, плашка «требуется действие», обновить. Действий шага в Ф1 нет.
export default function DealHeader({ info, onRefresh, refreshing, chatAction }: DealHeaderProps) {
  const { t } = useTranslation();
  const dealNumber = info.specNum ?? info.contractNum;
  const stageKey = forwardingStageTitleKey(info.processingStep ?? '');
  const stage = stageKey ? t(stageKey) : info.stage;
  const quality = headlineQuality(info.goodsProperties);
  const outcome = goodsSpecFactDiff(info); // цена товара: по спецификации → факт → разница (FCA, без НДС)
  const rub = t('app.deals.rub');
  const sellerName = formatOrgName(info.seller?.name);
  const buyerName = formatOrgName(info.buyer?.name);
  const hasRoute = info.loadingAddress != null || info.unloadingAddress != null;

  return (
    <Stack gap="sm">
      <Group justify="space-between" wrap="nowrap">
        <Anchor component={Link} to={ROUTES.APP_DEALS} c="gray.7" fz="sm" underline="never">
          <Group gap={4} wrap="nowrap">
            <IconArrowLeft size={16} />
            {t('app.deals.back')}
          </Group>
        </Anchor>
        {/* Статус — в строке «объём · цена · этап», а не здесь: на узком экране три элемента вместе с «назад»
            не помещались и обрезались. */}
        <Group gap="xs" wrap="nowrap">
          {chatAction}
          <Tooltip label={t('app.deals.refresh')}>
            <ActionIcon variant="default" onClick={onRefresh} loading={refreshing} aria-label={t('app.deals.refresh')}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <Stack gap={4}>
        <Group gap="xs" wrap="wrap" align="baseline">
          <Title order={3}>
            {dealNumber ? <NumberedId value={dealNumber} /> : '—'}
          </Title>
          {info.goodsType ? (
            <Text fz="lg" c="gray.8">
              · {info.goodsType}
            </Text>
          ) : null}
          {quality ? (
            <Text fz="sm" c="gray.7">
              · {formatProperty(quality)}
            </Text>
          ) : null}
        </Group>
        <Group gap="md" wrap="wrap" align="center">
          <DealStatusBadge status={info.bargainStatus} />
          {info.plannedWeight != null ? (
            <Text fz="sm" c="gray.8">
              {formatMoney(info.plannedWeight)} {t('app.deals.tons')}
            </Text>
          ) : null}
          {info.price != null ? (
            <Text fz="sm" c="gray.8">
              {formatMoney(info.price)} {t('app.deals.rubPerTon')}
              <NoNds />
            </Text>
          ) : null}
          {stage ? (
            <Badge variant="light" color="gray" tt="none" fw={500} radius="sm">
              {stage}
            </Badge>
          ) : null}
        </Group>
        {outcome.spec != null ? (
          <Group gap="md" wrap="wrap" align="baseline">
            <Text fz="sm" c="gray.7">
              {t('app.deals.header.bySpec')}{' '}
              <Text span fw={600} c="gray.8">
                {formatMoney(outcome.spec)} {rub}
              </Text>
              <NoNds />
            </Text>
            {outcome.fact != null ? (
              <Text fz="sm" c="gray.7">
                {t('app.deals.header.fact')}{' '}
                <Text span fw={600} c="gray.8">
                  {formatMoney(outcome.fact)} {rub}
                </Text>
                <NoNds />
              </Text>
            ) : null}
            {outcome.diff != null && Math.abs(outcome.diff) > 1 ? (
              <Group gap={4} wrap="nowrap" align="center">
                {outcome.diff > 0 ? (
                  <IconArrowUp size={15} color="var(--mantine-color-gray-6)" />
                ) : (
                  <IconArrowDown size={15} color="var(--mantine-color-gray-6)" />
                )}
                <Text fz="sm" c="gray.7">
                  {t('app.deals.header.difference')}{' '}
                  <Text span fw={600} c="gray.8">
                    {formatMoney(Math.abs(outcome.diff))} {rub}
                  </Text>
                  <NoNds />
                </Text>
              </Group>
            ) : null}
          </Group>
        ) : null}
        {(sellerName || buyerName || hasRoute) && (
          <Stack gap={4} mt={4}>
            {sellerName || buyerName ? (
              <Group gap={6} wrap="nowrap" align="center">
                <IconUsers size={15} color="var(--mantine-color-gray-5)" style={{ flexShrink: 0 }} />
                <Text fz="sm" c="gray.8">
                  {sellerName ?? '—'} → {buyerName ?? '—'}
                </Text>
              </Group>
            ) : null}
            {hasRoute ? (
              <Group gap={6} wrap="nowrap" align="flex-start">
                <IconMapPin size={15} color="var(--mantine-color-gray-5)" style={{ flexShrink: 0, marginTop: 3 }} />
                <Stack gap={2}>
                  <Text fz="sm" c="gray.7">
                    {t('app.deals.header.loading')}: {info.loadingAddress ?? '—'}
                  </Text>
                  <Text fz="sm" c="gray.7">
                    {t('app.deals.header.unloading')}: {info.unloadingAddress ?? '—'}
                    {info.distance != null ? ` · ${info.distance} ${t('app.deals.km')}` : ''}
                  </Text>
                </Stack>
              </Group>
            ) : null}
          </Stack>
        )}
      </Stack>

      {info.actionRequired ? (
        <Alert color="brand" variant="light" icon={<IconBolt size={18} />} py="xs">
          {t('app.deals.actionRequired')}
        </Alert>
      ) : info.bargainStatus === 'WAIT_BPMN' ? (
        <Alert color="gray" variant="light" icon={<IconAlertCircle size={18} />} py="xs">
          {t('app.deals.processing')}
        </Alert>
      ) : null}
    </Stack>
  );
}
